import { useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { SurfaceProps, CodeLang } from './types';
import { surfaceFilled } from './types';
import { highlight, beautify } from '@/lib/interactive/codeFormat';

/**
 * Код-редактор (строгая форма · Genie «редактор кода с бьютификацией»).
 * Техника «прозрачная textarea поверх подсвеченного <pre>» — переиспользует
 * токенайзер codeFormat.highlight() (python/sql/js/bash) и безопасный beautify().
 * Ноль внешних зависимостей. Гуттер с номерами строк, авто-рост по контенту,
 * Tab/Shift-Tab отступ, Enter держит уровень, авто-парные скобки/кавычки.
 * Кнопка «Форматировать» гоняет beautify (язык-осознанно, никогда не ломает код).
 */

const LANG_LABEL: Record<CodeLang, string> = { js: 'JS', sql: 'SQL', python: 'PYTHON', bash: 'BASH' };
const PAIRS: Record<string, string> = { '(': ')', '[': ']', '{': '}', '"': '"', "'": "'", '`': '`' };

export function CodeForm({ spec, value, disabled, onChange }: SurfaceProps) {
  const lang: CodeLang = spec.config?.codeLang ?? 'js';
  const code = typeof value?.code === 'string' ? (value.code as string) : '';
  const taRef = useRef<HTMLTextAreaElement>(null);
  const preRef = useRef<HTMLPreElement>(null);
  const caretRef = useRef<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [formatted, setFormatted] = useState(false);

  const emit = (next: string, caret?: number) => {
    if (caret != null) caretRef.current = caret;
    onChange({ code: next }, surfaceFilled(spec, { code: next }));
  };

  // Auto-grow to content + restore caret after a programmatic edit.
  useLayoutEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
    if (caretRef.current != null) {
      ta.selectionStart = ta.selectionEnd = caretRef.current;
      caretRef.current = null;
    }
  });

  const syncScroll = () => {
    const ta = taRef.current, pre = preRef.current;
    if (ta && pre) { pre.scrollLeft = ta.scrollLeft; pre.scrollTop = ta.scrollTop; }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const ta = taRef.current;
    if (!ta || disabled) return;
    const start = ta.selectionStart, end = ta.selectionEnd;

    if (e.key === 'Tab') {
      e.preventDefault();
      const lineStart = code.lastIndexOf('\n', start - 1) + 1;
      if (start !== end || e.shiftKey) {
        const before = code.slice(0, lineStart);
        const sel = code.slice(lineStart, end);
        const after = code.slice(end);
        if (e.shiftKey) {
          const dedented = sel.split('\n').map((l) => l.replace(/^ {1,2}/, '')).join('\n');
          emit(before + dedented + after, lineStart + dedented.length);
        } else {
          const indented = sel.split('\n').map((l) => `  ${l}`).join('\n');
          emit(before + indented + after, lineStart + indented.length);
        }
      } else {
        emit(`${code.slice(0, start)}  ${code.slice(end)}`, start + 2);
      }
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const lineStart = code.lastIndexOf('\n', start - 1) + 1;
      const indent = (/^[ \t]*/.exec(code.slice(lineStart, start)) ?? [''])[0];
      const prev = code[start - 1], next = code[start];
      const opener = prev === '{' || prev === '(' || prev === '[';
      const closerAfter =
        (prev === '{' && next === '}') || (prev === '(' && next === ')') || (prev === '[' && next === ']');
      if (opener && closerAfter) {
        const inner = `${indent}  `;
        emit(`${code.slice(0, start)}\n${inner}\n${indent}${code.slice(end)}`, start + 1 + inner.length);
      } else if (opener) {
        const insert = `\n${indent}  `;
        emit(code.slice(0, start) + insert + code.slice(end), start + insert.length);
      } else {
        const insert = `\n${indent}`;
        emit(code.slice(0, start) + insert + code.slice(end), start + insert.length);
      }
      return;
    }

    // Skip over an auto-inserted closing bracket.
    if ((e.key === ')' || e.key === ']' || e.key === '}') && start === end && code[start] === e.key) {
      e.preventDefault();
      ta.selectionStart = ta.selectionEnd = start + 1;
      return;
    }

    // Auto-close pairs on an empty selection.
    if (PAIRS[e.key] && start === end) {
      e.preventDefault();
      emit(code.slice(0, start) + e.key + PAIRS[e.key] + code.slice(end), start + 1);
    }
  };

  const runFormat = () => {
    const next = beautify(code, lang);
    emit(next, next.length);
    setFormatted(true);
    setTimeout(() => setFormatted(false), 1500);
  };

  const runCopy = () => {
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1200); };
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(done, done);
    } else { done(); }
  };

  const lineCount = code.length ? code.split('\n').length : 1;
  const gutter = Array.from({ length: lineCount }, (_, i) => i + 1).join('\n');
  const html = `${highlight(code, lang)}\n`;

  return (
    <div className="surf surf-code" data-testid="surf-code" data-lang={lang}>
      <div className="cf-bar">
        <span className="cf-lang">{LANG_LABEL[lang]}</span>
        <span className="cf-sp" />
        {!disabled && (
          <button type="button" className="cf-btn" data-testid="code-format" onClick={runFormat}>
            {formatted ? 'Отформатировано' : 'Форматировать'}
          </button>
        )}
        <button type="button" className="cf-btn" data-testid="code-copy" onClick={runCopy}>
          {copied ? 'Скопировано' : 'Копировать'}
        </button>
      </div>
      <div className="cf-editor">
        <pre className="cf-gutter" aria-hidden="true">{gutter}</pre>
        <div className="cf-code">
          <pre ref={preRef} className="cf-hl" aria-hidden="true" dangerouslySetInnerHTML={{ __html: html }} />
          <textarea
            ref={taRef}
            className="cf-ta"
            value={code}
            disabled={disabled}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            data-testid="code-input"
            aria-label="Редактор кода"
            onChange={(e) => emit(e.target.value)}
            onKeyDown={onKeyDown}
            onScroll={syncScroll}
          />
        </div>
      </div>
    </div>
  );
}
